import { supabase } from '@/lib/supabase'

const KEY_STABILITY = 'ops.opportunities_publish_stability_enabled'
const KEY_MINOR_RSI = 'ops.opportunities_minor_rsi_retarget_enabled'
const KEY_MINOR_RSI_EARLY = 'ops.opportunities_minor_rsi_early_confirm_enabled'
const KEY_STRICT = 'ops.opportunities_strict_publish_enabled'
const KEY_BLOCK_UNPUBLISH = 'ops.opportunities_block_unpublish_active'
const KEY_SIGNALS_MULTITF_GUARD = 'ops.signals_multitf_guard_enabled'

const KEY_MAX_60 = 'ops.opportunities_max_age_60_days'
const KEY_MAX_240 = 'ops.opportunities_max_age_240_days'
const KEY_MAX_720 = 'ops.opportunities_max_age_720_days'
const KEY_MAX_D = 'ops.opportunities_max_age_D_days'
const KEY_MAX_W = 'ops.opportunities_max_age_W_days'
const KEY_MAX_M = 'ops.opportunities_max_age_M_days'
const KEY_MAX_DEFAULT = 'ops.opportunities_max_age_default_days'

async function getBool(key: string, def = false): Promise<boolean> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', key)
    .maybeSingle()
  if (error) throw error
  const v = String((data?.value as string) || '').trim().toLowerCase()
  if (['1', 'true', 'yes', 'on'].includes(v)) return true
  if (['0', 'false', 'no', 'off'].includes(v)) return false
  return def
}

async function setBool(key: string, on: boolean): Promise<void> {
  const { error } = await supabase
    .from('app_settings')
    .upsert({ key, value: on ? 'true' : 'false' }, { onConflict: 'key' })
  if (error) throw error
}

async function getInt(key: string, def: number): Promise<number> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', key)
    .maybeSingle()
  if (error) throw error
  const raw = String((data?.value as string) || '').trim()
  const num = Number.parseInt(raw, 10)
  if (Number.isFinite(num) && num > 0) return num
  return def
}

async function setInt(key: string, value: number): Promise<void> {
  const safe = Number.isFinite(value) && value > 0 ? Math.floor(value) : null
  const str = safe != null ? String(safe) : ''
  const { error } = await supabase
    .from('app_settings')
    .upsert({ key, value: str }, { onConflict: 'key' })
  if (error) throw error
}

export async function getPublishStabilityEnabled(): Promise<boolean> {
  return getBool(KEY_STABILITY, true)
}
export async function setPublishStabilityEnabled(on: boolean): Promise<void> {
  return setBool(KEY_STABILITY, on)
}

export async function getMinorRSIRetargetEnabled(): Promise<boolean> {
  return getBool(KEY_MINOR_RSI, false)
}
export async function setMinorRSIRetargetEnabled(on: boolean): Promise<void> {
  return setBool(KEY_MINOR_RSI, on)
}

export async function getMinorRSIEarlyConfirmEnabled(): Promise<boolean> {
  return getBool(KEY_MINOR_RSI_EARLY, true)
}
export async function setMinorRSIEarlyConfirmEnabled(on: boolean): Promise<void> {
  return setBool(KEY_MINOR_RSI_EARLY, on)
}

export async function getStrictPublishEnabled(): Promise<boolean> {
  return getBool(KEY_STRICT, true)
}
export async function setStrictPublishEnabled(on: boolean): Promise<void> {
  return setBool(KEY_STRICT, on)
}

export async function getBlockUnpublishEnabled(): Promise<boolean> {
  return getBool(KEY_BLOCK_UNPUBLISH, true)
}
export async function setBlockUnpublishEnabled(on: boolean): Promise<void> {
  return setBool(KEY_BLOCK_UNPUBLISH, on)
}

export async function getSignalsMultiTfGuardEnabled(): Promise<boolean> {
  return getBool(KEY_SIGNALS_MULTITF_GUARD, false)
}
export async function setSignalsMultiTfGuardEnabled(on: boolean): Promise<void> {
  return setBool(KEY_SIGNALS_MULTITF_GUARD, on)
}

export type PublishMaxAgeSettings = {
  tf60: number
  tf240: number
  tf720: number
  tfD: number
  tfW: number
  tfM: number
  defaultDays: number
}

export async function getPublishMaxAgeSettings(): Promise<PublishMaxAgeSettings> {
  const [tf60, tf240, tf720, tfD, tfW, tfM, def] = await Promise.all([
    getInt(KEY_MAX_60, 2),
    getInt(KEY_MAX_240, 3),
    getInt(KEY_MAX_720, 7),
    getInt(KEY_MAX_D, 14),
    getInt(KEY_MAX_W, 21),
    getInt(KEY_MAX_M, 90),
    getInt(KEY_MAX_DEFAULT, 7),
  ])

  return {
    tf60,
    tf240,
    tf720,
    tfD,
    tfW,
    tfM,
    defaultDays: def,
  }
}

export async function setPublishMaxAgeSettings(settings: PublishMaxAgeSettings): Promise<void> {
  await Promise.all([
    setInt(KEY_MAX_60, settings.tf60),
    setInt(KEY_MAX_240, settings.tf240),
    setInt(KEY_MAX_720, settings.tf720),
    setInt(KEY_MAX_D, settings.tfD),
    setInt(KEY_MAX_W, settings.tfW),
    setInt(KEY_MAX_M, settings.tfM),
    setInt(KEY_MAX_DEFAULT, settings.defaultDays),
  ])
}
